/**
 * The Ask stream.
 *
 * `EventSource` cannot send an Authorization header, so this reads the SSE body
 * off `fetch` and parses the frames itself. Same event contract as the server:
 * context → token* → citations → done.
 */

import { getAccessToken } from '@/lib/supabase.client'
import type { AskSource } from '../types/brain.types'

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')

export interface AskHandlers {
  onContext?: (payload: { sources: AskSource[]; entity_ids: string[] }) => void
  onToken?: (text: string) => void
  onCitations?: (citations: AskSource[]) => void
  onError?: (message: string) => void
  onDone?: (payload: { refused: boolean; model?: string; reason?: string }) => void
}

function dispatch(event: string, raw: string, handlers: AskHandlers): void {
  let payload: Record<string, unknown> = {}
  try {
    payload = raw ? JSON.parse(raw) : {}
  } catch {
    return
  }

  switch (event) {
    case 'context':
      handlers.onContext?.(payload as never)
      break
    case 'token':
      handlers.onToken?.(String(payload.text ?? ''))
      break
    case 'citations':
      handlers.onCitations?.((payload.citations as AskSource[]) ?? [])
      break
    case 'error':
      handlers.onError?.(String(payload.message ?? 'The request failed.'))
      break
    case 'done':
      handlers.onDone?.(payload as never)
      break
  }
}

export async function askStream(
  graphId: string,
  question: string,
  handlers: AskHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const token = await getAccessToken().catch(() => null)

  const response = await fetch(
    `${API_BASE}/api/v1/brain/graphs/${graphId}/ask`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ question }),
      signal,
    },
  )

  if (!response.ok || !response.body) {
    handlers.onError?.(
      response.status === 401
        ? 'Your session expired. Sign in again.'
        : `Ask failed (${response.status}).`,
    )
    handlers.onDone?.({ refused: true, reason: 'http_error' })
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  // Frames are separated by a blank line; anything after the last one is a
  // partial frame and stays in the buffer until the rest arrives.
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let split = buffer.indexOf('\n\n')
    while (split !== -1) {
      const frame = buffer.slice(0, split)
      buffer = buffer.slice(split + 2)

      let event = 'message'
      const data: string[] = []
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) data.push(line.slice(5).trim())
      }
      if (data.length) dispatch(event, data.join('\n'), handlers)

      split = buffer.indexOf('\n\n')
    }
  }
}
